import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';

import LoginPage from './components/auth/LoginPage';
import Forms from './components/Forms';
import ProposalView from './components/ProposalView';
import DashboardLayout from './components/layout/DashboardLayout';
import CustomerFeedbackFormPage from './components/customerSentiment/forms/CustomerFeedbackFormPage';
import PublicCustomerFeedbackPage from './components/customerSentiment/forms/PublicCustomerFeedbackPage';
import { LazySquadStructure, LazyPeopleAnalytics, LazyAdminDashboard, LazyRequests } from './components/LazyComponents';
import PageAccessControl from './components/auth/PageAccessControl';
import MyInsightsPage from './pages/MyInsightsPage';
import MyTeamPage from './pages/MyTeamPage';
import UserFeedbackPage from './pages/UserFeedbackPage';
import CustomerSentimentPage from './pages/CustomerSentimentPage';
import MyFilesPage from './pages/MyFilesPage';
import DRIInsightsPage from './pages/DRIInsightsPage';
import UtilizationInsightsPage from './pages/UtilizationInsightsPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectsPortfolioPage from './pages/ProjectsPortfolioPage';
import AiChatbotPage from './pages/AiChatbotPage';
import SearceAssetsPage from './pages/SearceAssetsPage';
import ExecutiveSummaryPage from './pages/ExecutiveSummaryPage';
import HRStatsPage from './pages/HRStatsPage';
import GCPSandboxBillingPage from './pages/GCPSandboxBillingPage';
import SolutionStoriesPage from './pages/SolutionStoriesPage';
import ZohoTasksPage from './pages/ZohoTasksPage';
import SolutionStoryAdminPage from './pages/SolutionStoryAdminPage';

import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleBasedRedirect from './components/auth/RoleBasedRedirect';
import { AuthProvider } from './contexts/AuthContext';
import { FilterOptionsProvider } from './contexts/FilterOptionsContext';
import { initGA } from './utils/analytics';

initGA();
// Create a default theme with Searce branding
const theme = createTheme({
  palette: {
    primary: {
      main: '#0064ff', // Searce blue
      light: '#3385ff',
      dark: '#0047b3',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: ['Poppins', 'Arial', 'sans-serif'].join(','),
    h5: {
      fontWeight: 700,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 100, 255, 0.15)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontSize: '0.75rem',
          fontWeight: 600,
        },
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontSize: '0.75rem',
            fontWeight: 600,
          },
          '& .MuiTableCell-head strong': {
            fontSize: '0.75rem',
            fontWeight: 700,
          },
        },
      },
    },
  },
});

// Create a client with optimized cache lifetimes
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes default
      gcTime: 30 * 60 * 1000, // 30 minutes (increased for better caching)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
      refetchOnWindowFocus: false,
      refetchOnReconnect: false, // Don't refetch on reconnect for better performance
    },
  },
});

function App() {
  
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <AuthProvider>
              <Routes>
                {/* Public routes - No authentication required */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/public/feedback/:token" element={<PublicCustomerFeedbackPage />} />

                {/* Protected app: pathless layout parent so nested paths like /projects/portfolio match (avoid /* + inner Routes swallowing routes). */}
                <Route
                  element={
                    <ProtectedRoute>
                      <FilterOptionsProvider>
                        <DashboardLayout />
                      </FilterOptionsProvider>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={
                    <RoleBasedRedirect>
                      <PageAccessControl requiredPage="dashboard" fallbackPath="/my-insights">
                        <UtilizationInsightsPage />
                      </PageAccessControl>
                    </RoleBasedRedirect>
                  } />
                  <Route path="/projects/portfolio" element={
                    <PageAccessControl requiredPage="projects-portfolio">
                      <ProjectsPortfolioPage />
                    </PageAccessControl>
                  } />
                  <Route
                    path="/projects/executive-summary"
                    element={
                      <PageAccessControl requiredPage="executive-summary">
                        <ExecutiveSummaryPage />
                      </PageAccessControl>
                    }
                  />
                  <Route
                    path="/ai-chatbot"
                    element={
                      <PageAccessControl requiredPage="ai-chatbot">
                        <AiChatbotPage />
                      </PageAccessControl>
                    }
                  />
                  <Route path="/zoho-tasks" element={
                    <PageAccessControl requiredPage="zoho-tasks">
                      <ZohoTasksPage />
                    </PageAccessControl>
                  } />
                  <Route path="/projects" element={
                    <PageAccessControl requiredPage="projects">
                      <ProjectsPage />
                    </PageAccessControl>
                  } />
                  <Route path="/my-insights" element={
                    <PageAccessControl requiredPage="my-insights">
                      <MyInsightsPage />
                    </PageAccessControl>
                  } />
                  <Route path="/my-team" element={
                    <PageAccessControl requiredPage="my-team">
                      <MyTeamPage />
                    </PageAccessControl>
                  } />
                  <Route path="/people-analytics" element={
                    <PageAccessControl requiredPage="people-analytics">
                      <LazyPeopleAnalytics />
                    </PageAccessControl>
                  } />
                  <Route
                    path="/internal-project-request"
                    element={
                      <PageAccessControl requiredPage="internal-project-request">
                        <LazyRequests />
                      </PageAccessControl>
                    }
                  />
                  <Route path="/user-feedback" element={
                    <PageAccessControl requiredPage="user-feedback">
                      <UserFeedbackPage />
                    </PageAccessControl>
                  } />
                  <Route path="/customer-sentiment" element={
                    <PageAccessControl requiredPage="customer-sentiment">
                      <CustomerSentimentPage />
                    </PageAccessControl>
                  } />
                  <Route path="/my-files" element={
                    <PageAccessControl requiredPage="important-links">
                      <MyFilesPage />
                    </PageAccessControl>
                  } />
                  <Route path="/dri-insights" element={
                    <PageAccessControl requiredPage="dri-insights">
                      <DRIInsightsPage />
                    </PageAccessControl>
                  } />
                  <Route
                    path="/admin"
                    element={
                      <PageAccessControl requiredPage="admin">
                        <LazyAdminDashboard />
                      </PageAccessControl>
                    }
                  />
                  <Route path="/squad-structure"
                    element={
                      <PageAccessControl requiredPage="squad-structure">
                        <LazySquadStructure />
                      </PageAccessControl>
                    }
                  />
                  <Route path="/forms" element={
                    <PageAccessControl requiredPage="forms">
                      <Forms />
                    </PageAccessControl>
                  } />
                  <Route path="/customer-feedback" element={
                    <PageAccessControl requiredPage="customer-sentiment">
                      <CustomerFeedbackFormPage />
                    </PageAccessControl>
                  } />
                  <Route path="/searce-assets" element={<SearceAssetsPage />} />
                  <Route
                    path="/executive-summary"
                    element={<Navigate to="/projects/executive-summary" replace />}
                  />
                  <Route path="/hr-stats" element={
                    <PageAccessControl requiredPage="hr-stats">
                      <HRStatsPage />
                    </PageAccessControl>
                  } />
                  <Route path="/sandbox-billing" element={
                    <PageAccessControl requiredPage="sandbox-billing">
                      <GCPSandboxBillingPage />
                    </PageAccessControl>
                  } />
                  <Route path="/proposal/:id" element={<ProposalView />} />
                  <Route path="/requests" element={<Navigate to="/internal-project-request" replace />} />
                  <Route path="/solution-story" element={
                    <PageAccessControl requiredPage="solution-story">
                      <SolutionStoriesPage />
                    </PageAccessControl>
                  } />
                  {/* Deep link: /solution-story/SS-489 opens that story's modal on the same page. */}
                  <Route path="/solution-story/:storyId" element={
                    <PageAccessControl requiredPage="solution-story">
                      <SolutionStoriesPage />
                    </PageAccessControl>
                  } />
                  <Route path="/solution-stories/admin" element={
                    <PageAccessControl requiredPage="solution-story">
                      <SolutionStoryAdminPage />
                    </PageAccessControl>
                  } />
                </Route>

                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AuthProvider>
          </Router>
        </ThemeProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

export default App; 